import { useEffect, useState, useRef } from 'react';
import { Tooltip, HelpToggletip, TipPlacement } from 'ui/tooltip';
import { Divider } from 'ui/divider';
import kebabCase from 'lodash/kebabCase';
import { DefinitionWrapper } from 'modules/metricDefinitions/components/DefinitionWrapper';
import { colors } from 'ui/theme/colors';
import {
  AnalyticsCardContainer,
  CardHeader,
  ToggletipContainer,
  ValueText,
  TooltipHeading,
  StyledTooltip,
} from './AnalyticsCard.styles';
import { PERCENTAGE, NUMBER, CURRENCY, NULL } from './constants';
import {
  abbreviate,
  formatPercentage,
  compareScrollAndClientWidthSize,
  TEST_1_DECIMAL,
  TEST_3_DECIMALS,
} from './utils';
import { AnalyticsCardProps } from './types';

export const AnalyticsCard = ({
  title,
  TitleWrapper,
  value,
  valueType = NUMBER,
  valueTooltipContent,
  helpText,
  children,
  AnalyticsCardContainerComponent = AnalyticsCardContainer,
  metricDefFFOn,
}: AnalyticsCardProps) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const compareSize = () => {
    const shouldBeTruncated = compareScrollAndClientWidthSize(
      ref.current?.scrollWidth,
      ref.current?.clientWidth,
    );
    setIsTruncated(shouldBeTruncated);
  };

  // compare once and add resize listener on "componentDidMount"
  useEffect(() => {
    compareSize();
    window.addEventListener('resize', compareSize);
  }, []);

  // remove resize listener again on "componentWillUnmount"
  useEffect(
    () => () => {
      window.removeEventListener('resize', compareSize);
    },
    [],
  );

  let abbreviatedNumber;
  let formattedValue;
  if (valueType === NULL) {
    formattedValue = '--';
  } else {
    // minimumFractionDigits cannot exceed 19, otherwise the lack of precision leads to a range error
    const numberOfDecimals = Math.min(value.toString().split('.')?.[1]?.length || 0, 19);
    formattedValue = value.toLocaleString(undefined, { minimumFractionDigits: numberOfDecimals });
  }
  if (valueType === PERCENTAGE) {
    if (TEST_3_DECIMALS.test(`${value}`)) {
      abbreviatedNumber = formatPercentage(value);
      formattedValue = `${formattedValue}%`;
    } else {
      formattedValue = formatPercentage(value);
    }
  } else {
    const number = Number(value);
    if (number >= 1e6) {
      // When the value is greater than a million, the content width becomes greater than
      // the card width that's why we're using abbreviation for the large numbers
      abbreviatedNumber = abbreviate(number);
    } else if (TEST_1_DECIMAL.test(`${number}`) && valueType !== CURRENCY) {
      abbreviatedNumber = Number(number.toFixed(0)).toLocaleString();
    }
  }
  if (valueType === CURRENCY) {
    if (abbreviatedNumber) {
      abbreviatedNumber = `$${abbreviatedNumber}`;
    }
    formattedValue = `$${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  let tooltipContent;
  let displayValue = formattedValue;
  let tooltipPlacement = TipPlacement.Right;

  if (abbreviatedNumber) {
    if (valueType !== PERCENTAGE) {
      tooltipContent = formattedValue;
    }
    displayValue = abbreviatedNumber;
  }

  if (valueTooltipContent) {
    tooltipPlacement = TipPlacement.Bottom;
    tooltipContent = tooltipContent ? (
      <>
        <TooltipHeading>{tooltipContent}</TooltipHeading>
        <Divider />
        {valueTooltipContent}
      </>
    ) : (
      valueTooltipContent
    );
  }

  const Title = metricDefFFOn ? <DefinitionWrapper>{title}</DefinitionWrapper> : title;

  return (
    <AnalyticsCardContainerComponent data-test={`analytics-card-${kebabCase(title)}`}>
      {helpText && (
        <ToggletipContainer data-test={`tooltip-container-${kebabCase(title)}`}>
          <HelpToggletip iconProps={{ color: colors.iconPrimary }} tooltipContent={helpText} />
        </ToggletipContainer>
      )}
      <StyledTooltip tooltipContent={title} isEnabled={isTruncated}>
        <CardHeader ref={ref} data-test={`analytics-card-header-${kebabCase(title)}`}>
          {TitleWrapper ? <TitleWrapper>{Title}</TitleWrapper> : Title}
        </CardHeader>
      </StyledTooltip>
      <Tooltip
        isEnabled={!!tooltipContent}
        tooltipContent={tooltipContent}
        placement={tooltipPlacement}
      >
        <ValueText data-test={`analytics-card-value-${kebabCase(title)}`}>{displayValue}</ValueText>
      </Tooltip>
      {children}
    </AnalyticsCardContainerComponent>
  );
};

export default AnalyticsCard;
